/**
 * Project money summary — budget vs actual as four server-side totals
 * (`@releve/shared` `schemas/projectMoney.ts`, `apps/api/src/routes/projects/projects.ts`):
 *
 *   GET /api/v1/projects/orgs/:orgId/projects/:projectId/money-summary → { summary }
 *   GET /api/v1/projects/orgs/:orgId/projects/money-summary            → { items }
 *
 * Open to every org role, project_manager included — the one place a role
 * without the `accounting` scope sees project money, and only as totals (no
 * ledger rows). The project Overview / Interactions tiles and the dashboard
 * portfolio strip read it; the raw-row hooks (`useEntityLedgerTransactions`,
 * `useProjectRevenue`) stay inside the project Accounting sub-tabs.
 *
 * Caching: keys sit under `["projects", orgId]`, so every project write that
 * invalidates the project list refreshes the totals too. Every write that
 * moves a total — expense / AP bill / AR invoice / journal writers and the
 * three revenue-source mutations — calls `invalidateProjectMoney`.
 * staleTime 60 s.
 */
import { useQuery, type QueryClient } from "@tanstack/react-query";
import type {
  ProjectMoneySummary,
  ProjectMoneySummaryListResponse,
  ProjectMoneySummaryResponse,
} from "@releve/shared";
import { authedGet } from "@/lib/api";
import { useOrganization } from "@/contexts/OrganizationContext";

export const projectMoneyKeys = {
  all: (orgId: string | undefined) => ["projects", orgId, "money-summary"] as const,
  list: (orgId: string | undefined) => ["projects", orgId, "money-summary", "list"] as const,
  detail: (orgId: string | undefined, projectId: string | undefined) =>
    ["projects", orgId, "money-summary", "detail", projectId] as const,
};

const STALE_MS = 60 * 1000;

/**
 * Refetch the project money summaries of an org — call it from every writer
 * that can move a project's spend or revenue (an expense / bill / invoice /
 * journal carrying a project, a revenue source).
 */
export function invalidateProjectMoney(queryClient: QueryClient, orgId: string | undefined): void {
  if (!orgId) return;
  void queryClient.invalidateQueries({ queryKey: projectMoneyKeys.all(orgId) });
}

const basePath = (orgId: string) => `/api/v1/projects/orgs/${orgId}/projects`;

/** One project's budget, spent and revenue totals. */
export function useProjectMoneySummary(projectId: string | undefined) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery({
    queryKey: projectMoneyKeys.detail(orgId, projectId),
    queryFn: async (): Promise<ProjectMoneySummary> => {
      const res = await authedGet<ProjectMoneySummaryResponse>(
        `${basePath(orgId!)}/${projectId}/money-summary`,
      );
      return res.summary;
    },
    enabled: !!orgId && !!projectId,
    staleTime: STALE_MS,
  });
}

/** Every project's totals in the org, keyed by project id. */
export function useProjectMoneySummaries() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery({
    queryKey: projectMoneyKeys.list(orgId),
    queryFn: async (): Promise<Record<string, ProjectMoneySummary>> => {
      const res = await authedGet<ProjectMoneySummaryListResponse>(`${basePath(orgId!)}/money-summary`);
      return Object.fromEntries(res.items.map((item) => [item.project_id, item]));
    },
    enabled: !!orgId,
    staleTime: STALE_MS,
  });
}

/** Share of the budget spent, in percent — 0 without a positive budget. */
export function budgetUsedPercent(summary: Pick<ProjectMoneySummary, "budget" | "spent"> | undefined): number {
  if (!summary?.budget || summary.budget <= 0) return 0;
  return (summary.spent / summary.budget) * 100;
}
